import { useState, useEffect, createContext, useContext } from 'react';
import { orgApi } from './api';
import { useAuthStore } from '../store/useAuthStore';

export interface PlanInfo {
  plan: string;
  seatLimit: number | null;
  planOverride: boolean;
  overrideNotes: string | null;
  trialEndsAt: string | null;
  usedSeats: number;
  effectiveLimit: number | null;
  hasActiveSubscription: boolean;
  stripeSubscriptionId: string | null;
  metricUnit?: string; // 'hours' | 'points'
  // Workload tidy-up window (days): Done-stage tickets completed more than
  // this many days ago are tucked off the board behind the disclosed
  // "+ N completed — hidden" note. null/undefined = product default (14);
  // 0 = never hide. Admin-set on the Statuses page.
  staleDoneDays?: number | null;
}

export type MetricUnit = 'hours' | 'points';
/** Short capacity-unit label for the active org metric ('h' or 'pts'). */
export function unitShort(u: MetricUnit): string { return u === 'points' ? 'pts' : 'h'; }

type Feature = 'intelligence' | 'export' | 'customFields' | 'branding' | 'jiraWriteBack' | 'apiKeys' | 'webhooks' | 'importContinuity' | 'automations' | 'inboundWebhooks';

const FEATURE_GATES: Record<Feature, string[]> = {
  intelligence:  ['team', 'business', 'enterprise'],
  // Import Refresh Model 1.0 — the "what changed since your last upload" delta
  // report + archived-items restore. Mirrors the server gate in lib/plans.ts;
  // the upload-mode choice + safe archive mechanics are NOT gated (every tier).
  importContinuity: ['team', 'business', 'enterprise'],
  export:        ['business', 'enterprise'],
  customFields:  ['business', 'enterprise'],
  branding:      ['business', 'enterprise'],
  jiraWriteBack: ['business', 'enterprise'],
  // The Avium API (machine credentials) is Enterprise-only — mirrors the server
  // gate in server/src/lib/plans.ts.
  apiKeys:       ['enterprise'],
  // Avium API for Enterprise (Phase 2 — outbound webhooks). Same Enterprise gate.
  webhooks:      ['enterprise'],
  automations:   ['enterprise'],
  inboundWebhooks: ['enterprise'],
};

export function canAccessFeature(plan: PlanInfo | null, feature: Feature): boolean {
  if (!plan) return false;
  if (plan.planOverride) return true;
  return FEATURE_GATES[feature]?.includes(plan.plan) ?? false;
}

export interface PlanContextValue {
  plan: PlanInfo | null;
  loading: boolean;
  refresh: () => void;
  can: (feature: Feature) => boolean;
  /** Active capacity metric unit, org-wide. Defaults to 'hours'. */
  metricUnit: MetricUnit;
  /** Workload tidy-up window in days (0 = never hide). Defaults to 14. */
  staleDoneDays: number;
}

export const PlanContext = createContext<PlanContextValue>({
  plan: null,
  loading: true,
  refresh: () => {},
  can: () => false,
  metricUnit: 'hours',
  staleDoneDays: 14,
});

export function usePlan(): PlanContextValue {
  return useContext(PlanContext);
}

export function usePlanState(): PlanContextValue {
  const orgId = useAuthStore(s => s.org?.id);
  const [plan, setPlan] = useState<PlanInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    setLoading(true);
    orgApi.plan()
      .then(r => setPlan(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [tick, orgId]);

  const refresh = () => setTick(t => t + 1);
  const can = (feature: Feature) => canAccessFeature(plan, feature);
  const metricUnit: MetricUnit = plan?.metricUnit === 'points' ? 'points' : 'hours';
  const staleDoneDays = typeof plan?.staleDoneDays === 'number' ? plan.staleDoneDays : 14;

  return { plan, loading, refresh, can, metricUnit, staleDoneDays };
}
